import { z } from 'zod';
import { isValidCnpj, isValidCpf, normalizeTaxId } from './tax-id.js';

/**
 * Documento do titular da conta (#494): pessoa física (CPF) ou empresa (CNPJ).
 * Fica no dono, como o cliente do Asaas que paga a assinatura, e vale para
 * todos os estacionamentos dele.
 */
export type TaxIdKind = 'cpf' | 'cnpj';

export const TAX_ID_KIND_LABEL: Record<TaxIdKind, string> = {
  cpf: 'Pessoa física (CPF)',
  cnpj: 'Empresa (CNPJ)',
};

const LEGAL_NAME_MAX = 160;

/** Tipo pelo tamanho do documento já normalizado: 11 é CPF, 14 é CNPJ. */
export const taxIdKindOf = (taxId: string): TaxIdKind => (taxId.length === 11 ? 'cpf' : 'cnpj');

const punctuate = (value: string, cuts: number[], marks: string[]): string => {
  let out = value.slice(0, cuts[0]);
  for (let i = 0; i < marks.length; i++) {
    const part = value.slice(cuts[i], cuts[i + 1]);
    if (part) out += `${marks[i]}${part}`;
  }
  return out;
};

/** Pontua enquanto digita: 000.000.000-00 ou 00.000.000/0000-00, com o CNPJ alfanumérico. */
export const formatTaxId = (raw: string, kind: TaxIdKind): string => {
  if (kind === 'cpf') {
    const digits = raw.replace(/\D/g, '').slice(0, 11);
    return punctuate(digits, [3, 6, 9, 11], ['.', '.', '-']);
  }
  const chars = normalizeTaxId(raw).slice(0, 14);
  return punctuate(chars, [2, 5, 8, 12, 14], ['.', '.', '/', '-']);
};

/**
 * Para mostrar na tela (LGPD): do CPF só os dígitos do meio; o CNPJ é público
 * e aparece inteiro.
 */
export const displayTaxId = (taxId: string, kind: TaxIdKind): string =>
  kind === 'cpf' ? `***.${taxId.slice(3, 6)}.${taxId.slice(6, 9)}-**` : formatTaxId(taxId, 'cnpj');

export const ownerDocumentSchema = z
  .object({
    kind: z.enum(['cpf', 'cnpj']),
    taxId: z.string().max(32).transform(normalizeTaxId),
    /** Razão social. Só a empresa tem; na pessoa física vale o nome do dono. */
    legalName: z
      .string()
      .trim()
      .max(LEGAL_NAME_MAX, `Use até ${LEGAL_NAME_MAX} caracteres.`)
      .nullish(),
  })
  .superRefine((v, ctx) => {
    const valid = v.kind === 'cpf' ? isValidCpf(v.taxId) : isValidCnpj(v.taxId);
    if (!valid) {
      ctx.addIssue({
        code: 'custom',
        path: ['taxId'],
        message: `${v.kind === 'cpf' ? 'CPF' : 'CNPJ'} inválido. Confira os números.`,
      });
    }
    if (v.kind === 'cnpj' && !v.legalName) {
      ctx.addIssue({ code: 'custom', path: ['legalName'], message: 'Informe a razão social.' });
    }
  })
  .transform((v) => ({ ...v, legalName: v.kind === 'cnpj' ? (v.legalName ?? null) : null }));

export type OwnerDocumentInput = z.input<typeof ownerDocumentSchema>;
export type OwnerDocument = z.output<typeof ownerDocumentSchema>;
